import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface PageBannerProps {
  title: string;
  subtitle: string;
  breadcrumb: string;
  imageUrl: string;
  accentWord?: string;
}

export default function PageBanner({ title, subtitle, breadcrumb, imageUrl, accentWord }: PageBannerProps) {
  const { ref, visible } = useScrollAnimation(0);

  const renderTitle = () => {
    if (!accentWord) return title;
    const parts = title.split(new RegExp(`(${accentWord})`, 'i'));
    return parts.map((part, i) =>
      part.toLowerCase() === accentWord.toLowerCase()
        ? <span key={i} className="text-primary-light">{part}</span>
        : part
    );
  };

  return (
    <section className="relative w-full h-[420px] sm:h-[480px] flex items-end overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center scale-105 transition-transform duration-[8000ms]"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/90 via-primary/75 to-primary-dark/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-transparent to-black/30" />

      {/* Diagonal accent */}
      <div className="absolute bottom-0 right-0 w-1/3 h-full opacity-10 pointer-events-none"
        style={{ background: 'linear-gradient(135deg, transparent 50%, #2A5BA8 50%)' }} />

      <div
        ref={ref}
        className={`relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 pb-12 sm:pb-14 transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="flex items-center gap-2 mb-4">
          <a href="/" className="text-white/50 hover:text-primary-light text-xs uppercase tracking-widest transition-colors duration-200 cursor-pointer">Home</a>
          <i className="ri-arrow-right-s-line text-white/30 text-sm"></i>
          <span className="text-primary-light text-xs uppercase tracking-widest">{breadcrumb}</span>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-0.5 bg-primary-light" />
          <span className="text-primary-light text-xs font-bold uppercase tracking-[0.3em]">Downs Construction LLC</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase text-white leading-tight tracking-tight mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {renderTitle()}
        </h1>
        <p className="text-blue-200/80 text-sm sm:text-base max-w-xl leading-relaxed">{subtitle}</p>
      </div>

      {/* Bottom angle cut */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 40" fill="none" preserveAspectRatio="none" className="w-full h-8 sm:h-10" style={{ display: 'block' }}>
          <polygon points="0,40 1440,0 1440,40" fill="#fafaf8" />
        </svg>
      </div>
    </section>
  );
}
